import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchMentionSuggestions } from "./commentsApi";
import { getActiveMentionQuery, mentionInsertText } from "./commentMentions";
import "./commentMentions.css";

export default function CommentMentionTextarea({
  value,
  onChange,
  rows = 4,
  className = "project-comments-textarea",
  placeholder,
  disabled = false,
}) {
  const rootRef = useRef(null);
  const textareaRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [mentionCtx, setMentionCtx] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const fetchSeq = useRef(0);

  const syncMentionContext = useCallback(() => {
    const el = textareaRef.current;
    if (!el || disabled) {
      setMentionCtx(null);
      setMenuOpen(false);
      return;
    }
    const ctx = getActiveMentionQuery(el.value, el.selectionStart);
    setMentionCtx(ctx);
    setMenuOpen(Boolean(ctx));
    if (ctx) setActiveIndex(0);
  }, [disabled]);

  useEffect(() => {
    if (!menuOpen || !mentionCtx) {
      setSuggestions([]);
      setLoadingSuggestions(false);
      return undefined;
    }
    const seq = ++fetchSeq.current;
    setLoadingSuggestions(true);
    const timer = window.setTimeout(() => {
      fetchMentionSuggestions(mentionCtx.query)
        .then((users) => {
          if (fetchSeq.current !== seq) return;
          setSuggestions(users);
          setActiveIndex(0);
        })
        .catch(() => {
          if (fetchSeq.current !== seq) return;
          setSuggestions([]);
        })
        .finally(() => {
          if (fetchSeq.current === seq) setLoadingSuggestions(false);
        });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [menuOpen, mentionCtx]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const applyMention = useCallback(
    (user) => {
      const el = textareaRef.current;
      if (!el || !mentionCtx) return;
      const insert = `${mentionInsertText(user)} `;
      const nextValue = `${value.slice(0, mentionCtx.start)}${insert}${value.slice(el.selectionStart)}`;
      const nextCursor = mentionCtx.start + insert.length;
      onChange(nextValue);
      setMenuOpen(false);
      setMentionCtx(null);
      window.requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(nextCursor, nextCursor);
      });
    },
    [mentionCtx, onChange, value]
  );

  const onTextareaChange = (e) => {
    onChange(e.target.value);
    window.requestAnimationFrame(syncMentionContext);
  };

  const onKeyDown = (e) => {
    if (!menuOpen || !suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
      return;
    }
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      applyMention(suggestions[activeIndex]);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setMenuOpen(false);
    }
  };

  return (
    <div className="project-comments-mention-wrap" ref={rootRef}>
      <textarea
        ref={textareaRef}
        className={className}
        rows={rows}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={onTextareaChange}
        onKeyDown={onKeyDown}
        onClick={syncMentionContext}
        onKeyUp={syncMentionContext}
        onFocus={syncMentionContext}
      />
      {menuOpen ? (
        <div className="project-comments-mention-menu" role="listbox" aria-label="Mention suggestions">
          {loadingSuggestions && !suggestions.length ? (
            <p className="project-comments-mention-empty">Loading users…</p>
          ) : null}
          {!loadingSuggestions && !suggestions.length ? (
            <p className="project-comments-mention-empty">No matching users.</p>
          ) : null}
          {suggestions.map((user, idx) => {
            const handle = mentionInsertText(user).slice(1);
            const primaryLabel = user.firstName || user.fullName || handle;
            const subtitleParts = [`@${handle}`];
            if (user.fullName && user.fullName !== primaryLabel) subtitleParts.push(user.fullName);
            else if (user.email) subtitleParts.push(user.email);
            return (
              <button
                key={user.email}
                type="button"
                role="option"
                aria-selected={idx === activeIndex}
                className={`project-comments-mention-option${idx === activeIndex ? " is-active" : ""}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => applyMention(user)}
              >
                <span className="project-comments-mention-option-handle">{primaryLabel}</span>
                <span className="project-comments-mention-option-meta">{subtitleParts.join(" · ")}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
